<!-- 검색 조건 입력 컴포넌트 (공통) -->
<template>
  <v-row>
    <v-col>
      <BaseCard :title="$text('index.search')">
        <v-table>
          <colgroup>
            <col v-for="(width, idx) in colWidths" :key="idx" :style="{ width }" />
          </colgroup>
          <tbody>
            <tr v-for="(row, rowIndex) in searchRows" :key="rowIndex">
              <template v-for="(field, _fieldIndex) in row" :key="_fieldIndex">
                <th>{{ field.label }}</th>
                <td :colspan="field.colspan || 1">
                  <!-- 텍스트 입력 필드 -->
                  <template v-if="field.type === SearchType.text">
                    <BaseInput
                      v-model="searchParam[field.key as string] as string"
                      :placeholder="
                        typeof field.placeholder === 'string'
                          ? field.placeholder
                          : $text('index.partialSearch')
                      "
                      @keyup.enter="() => props.onClickSearch?.()"
                    >
                    </BaseInput>
                  </template>

                  <!-- 날짜 입력 필드 -->
                  <template v-else-if="field.type === SearchType.date">
                    <div class="d-flex align-center">
                      <!-- 1개 -->
                      <template v-if="field.key.length === 1">
                        <Base.DateInput v-model="searchParam![field.key[0]] as string" />
                      </template>
                      <!-- 2개 -->
                      <template v-if="field.key.length === 2">
                        <Base.DateInput
                          v-model="searchParam[field.key[0]] as string"
                          @update:model-value="
                            fnCompareDate(field.key[0] as string, field.key as string[])
                          "
                          :placeholder="
                            typeof field.placeholder === 'string'
                              ? field.placeholder
                              : $text('date.strDate')
                          "
                        />
                        <span class="mx-1">~</span>
                        <Base.DateInput
                          v-model="searchParam![field.key[1]] as string"
                          @update:model-value="
                            fnCompareDate(field.key[1] as string, field.key as string[])
                          "
                          :placeholder="
                            typeof field.placeholder === 'string'
                              ? field.placeholder
                              : $text('date.endDate')
                          "
                        />
                      </template>
                    </div>
                  </template>

                  <!-- 라디오 버튼 그룹 -->
                  <template v-else-if="field.type === SearchType.radio">
                    <div class="v-radio">
                      <Base.Radio
                        v-model="searchParam![field.key as string] as string"
                        :options="field.options"
                        inline
                      />
                    </div>
                  </template>

                  <!-- 체크박스 -->
                  <template v-else-if="field.type === SearchType.checkbox">
                    <div class="d-ruby align-center">
                      <!-- 전체 선택 체크박스 -->
                      <Base.Checkbox
                        :is-all="true"
                        v-model="searchParam![field.key as string] as string[]"
                        :options="field.options"
                        :label="$text('checkbox.all')"
                      />
                      <Base.Checkbox
                        :is-all="false"
                        v-model="searchParam![field.key as string] as string[]"
                        :options="field.options"
                      />
                    </div>
                  </template>

                  <!-- 숫자 입력 필드 -->
                  <template v-else-if="field.type === SearchType.number">
                    <template v-if="field.key.length === 1">
                      <BaseInput
                        v-model="searchParam![field.key[0]] as number"
                        type="number"
                        @keydown="preventExponent"
                        @keyup.enter="() => props.onClickSearch?.()"
                        hide-spin-buttons
                        hide-details
                        :placeholder="
                          typeof field.placeholder === 'string' ? field.placeholder : ''
                        "
                      >
                      </BaseInput>
                    </template>
                    <template v-if="field.key.length === 2">
                      <div class="d-flex align-center">
                        <BaseInput
                          v-model="searchParam![field.key[0]] as number"
                          type="number"
                          @keydown="preventExponent"
                          @keyup.enter="() => props.onClickSearch?.()"
                          hide-spin-buttons
                          autocomplete="off"
                          hide-details
                          :placeholder="field.placeholder![0]"
                        >
                        </BaseInput>
                        <span class="mx-1">~</span>
                        <BaseInput
                          v-model="searchParam![field.key[1]] as number"
                          type="number"
                          @keydown="preventExponent"
                          @keyup.enter="() => props.onClickSearch?.()"
                          hide-spin-buttons
                          autocomplete="off"
                          hide-details
                          :placeholder="field.placeholder![1]"
                        >
                        </BaseInput>
                      </div>
                    </template>
                  </template>

                  <!-- 자동완성 -->
                  <template v-else-if="field.type === SearchType.autocomplete">
                    <Base.Select
                      v-model="searchParam![field.key as string] as string"
                      :placeholder="
                        typeof field.placeholder === 'string'
                          ? field.placeholder
                          : $text('common.all')
                      "
                      :options="field.options"
                      :disabled="field.disabled"
                    >
                    </Base.Select>
                  </template>
                </td>
              </template>
            </tr>
          </tbody>
        </v-table>
        <div class="d-flex pt-4 justify-end">
          <Base.Button ref="resetBtn" class="mr-2" @click="() => onClickReset()">
            {{ $text("button.reset") + "(" + keyMap.f3.toUpperCase() + ")" }}
          </Base.Button>
          <template v-if="props.onClickSearch">
            <Base.Button
              ref="searchBtn"
              variant="flat"
              color="primary"
              @click="() => props.onClickSearch?.()"
              >{{ $text("button.search") + "(" + keyMap.f4.toUpperCase() + ")" }}
            </Base.Button>
          </template>
        </div>
      </BaseCard>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { Base } from "@/common/base-components/BaseFactory.ts";
import { global, i18n } from "@/app/plugins/global";
import { registerHotkey } from "@/app/util/HotKeyUtil";
import { KeyMap, SearchType } from "@/types/Enums";

interface SearchField {
  label: string;
  key: string | string[];
  type: SearchType;
  oneLine?: boolean;
  colspan?: number;
  placeholder?: string | string[];
  options?: Array<{ label: string; value: string }>;
  disabled?: boolean;
}

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const { $text } = i18n();
const vm = global();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  searchFields: {
    type: Array<SearchField>,
    required: true,
    default: () => [],
  },
  onClickSearch: {
    type: Function,
    required: true,
  },
  column: {
    type: Number,
    required: false,
    default: 4,
  },
});

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const resetBtn = ref();
const searchBtn = ref();
const keyMap = { f3: KeyMap.f3, f4: KeyMap.f4 };
const searchParam = defineModel<Record<string, unknown>>("searchParam", { required: true });
const initialValues = ref<Record<string, unknown>>({});
let unwatch: (() => void) | null = null;
let searchHotkey: (() => void) | null = null;
let clearHotkey: (() => void) | null = null;

/* ------------------------------------------------------------
                          Computed Properties
   ------------------------------------------------------------ */
/** 검색 행 데이터 */
const searchRows = computed(() => {
  const rows: SearchField[][] = [];

  let column = props.column;

  if (props.column) {
    column = props.column / 2;
  }

  const newFields = props.searchFields;

  let currentRow: SearchField[] = [];
  for (let i = 0; i < newFields.length; i++) {
    const field = newFields[i];
    if (field.oneLine || (currentRow.length > 0 && currentRow.length % column === 0)) {
      if (currentRow.length > 0) {
        rows.push([...currentRow]);
        currentRow = [];
      }
    }
    currentRow.push(field);
    if (field.oneLine) {
      rows.push([...currentRow]);
      currentRow = [];
    }
  }
  if (currentRow.length > 0) {
    rows.push([...currentRow]);
  }
  return rows;
});

/** column 넓이 동적 설정  */
const colWidths = computed(() => {
  const widths = [];
  for (let i = 0; i < props.column; i++) {
    if (props.column === 4) {
      widths.push(i % 2 === 1 ? "35%" : "15%");
    } else if (props.column === 6) {
      widths.push(i % 2 === 1 ? "23%" : "10%");
    }
  }
  return widths;
});

/* ------------------------------------------------------------
                          Methods
   ------------------------------------------------------------ */
/** 버튼 클릭 시 실행 메서드 */
const fnClickEvent = (el: HTMLElement) => {
  el.click();
  el.focus();
  setTimeout(() => {
    el.blur();
  }, 100);
};

/** 리셋 버튼 클릭 시 실행 메서드 */
const onClickReset = () => {
  Object.assign(searchParam.value, JSON.parse(JSON.stringify(initialValues.value)));
  vm.$toast($text("message.resetSuccess"), "success");
};

/** 날짜 검사 */
const fnCompareDate = (key: string, keyPair: string[]) => {
  const [startKey, endKey] = keyPair;
  const startDate = searchParam.value?.[startKey] as string;
  const endDate = searchParam.value?.[endKey] as string;
  vm.$compareDate(startDate, endDate, () => {
    nextTick(() => {
      searchParam.value[key] = null;
    });
  });
};

/** type: number 'e' 입력 제한 */
const preventExponent = (e: KeyboardEvent) => {
  if (e.key.toLowerCase() === "e") {
    e.preventDefault();
  }
};

/** 초기 값 설정 */
const setupInitialValues = () => {
  unwatch = watch(
    () => searchParam.value,
    (newVal) => {
      if (Object.keys(newVal).length > 0 && Object.keys(initialValues.value).length === 0) {
        initialValues.value = JSON.parse(JSON.stringify(newVal));
        if (unwatch) {
          unwatch();
          unwatch = null;
        }
      }
    },
    { deep: true, immediate: true }
  );
};

/* ------------------------------------------------------------
                          Lifecycle
   ------------------------------------------------------------ */
onMounted(async () => {
  clearHotkey = registerHotkey(
    keyMap.f3,
    (e: KeyboardEvent) => {
      e.preventDefault();
      const el = resetBtn.value?.$el;
      fnClickEvent(el);
    },
    { debounce: 200 }
  );
  searchHotkey = registerHotkey(
    keyMap.f4,
    (e: KeyboardEvent) => {
      e.preventDefault();
      const el = searchBtn.value?.$el;
      fnClickEvent(el);
    },
    { debounce: 200 }
  );
  setupInitialValues();
});

onUnmounted(() => {
  if (searchHotkey) searchHotkey();
  if (clearHotkey) clearHotkey();
  if (unwatch) unwatch();
});
</script>
